import type { ComponentChild, FunctionComponent } from 'preact';

import ColorTile from 'Components/smallComponents/colorTile';
import type { TemplateFunction } from 'Components/types';
import type { discard, discardQuery, filterQueryResetRequested, toggleColor } from 'Core/actions/request';
import type { SearchOptions } from 'Core/reducers/search/lastSearchOptions';
import { useDispatch } from 'Core/store';
import { FacetValue, type FacetValueFull, WeightedColor } from 'Models/index';
import facetsConfig from 'Models/uiConfig/facetsConfig';
import { DisplayMode } from 'Modules/serverApi/types';
import { isTermColor, isTermRanged } from 'Modules/termType';
import { cloneSafe } from 'Utils/components';
import { convertDecimalToImperial, getRangeFiniteness, getRangeFromValue } from 'Utils/ranged';

type ChipDiscardAction =
  | ReturnType<typeof discard>
  | ReturnType<typeof discardQuery>
  | ReturnType<typeof filterQueryResetRequested>
  | ReturnType<typeof toggleColor>;

export type Params =
  | ({ template: 'rangeChip'; selectedRange: string[] } & ReturnType<typeof getRangeFiniteness> &
      FacetValueFull)
  | ({
      template: 'colorChip';
      percent: number | string;
      color: string;
      colorTile: ComponentChild;
    } & FacetValueFull)
  | ({ template: 'reviewChip' } & FacetValueFull)
  | ({ template: 'chip' } & FacetValueFull);

export interface Props {
  template: TemplateFunction<Params>;
  value: FacetValueFull;
  discardValue: (value: FacetValueFull, options: SearchOptions) => ChipDiscardAction;
}

const Chip: FunctionComponent<Props> = ({ template, value, discardValue }) => {
  const dispatch = useDispatch();
  const onClick = () => dispatch(discardValue(value, { mayDiscardValue: true }));
  const component = template.call(getParams(value));

  return cloneSafe(component, null, {
    onClick,
    key: FacetValue.valueKey(value),
  });
};

export default Chip;

function getParams(entry: FacetValueFull): Params {
  const { field, value, term, displayMode } = entry;
  const baseParams = {
    ...entry,
    name: FacetValue.translateFacetTitle(field, entry.name),
    value: FacetValue.translateFacetValue(field, term, value),
  };

  if (field !== 'query') {
    if (isTermRanged(term) && displayMode !== DisplayMode.Ranges) {
      const selectedValues = getRangeFromValue(value, term);
      const selectedRange = selectedValues.map((v) =>
        facetsConfig.useImperialForField(field) ? convertDecimalToImperial(v) : v,
      );
      return {
        ...baseParams,
        selectedRange,
        ...getRangeFiniteness(selectedValues),
        template: 'rangeChip',
      };
    }

    if (isTermColor(term)) {
      const { color, weight: percent } = new WeightedColor(term);
      const colorTile = <ColorTile color={color} />;
      return {
        ...baseParams,
        percent,
        color,
        colorTile,
        template: 'colorChip',
      };
    }

    if (field === 'review') {
      return { ...baseParams, template: 'reviewChip' };
    }
  }

  return { ...baseParams, template: 'chip' };
}
